import { useCallback, useEffect, useMemo, useState } from "react";
import { ResetFiltersButton } from "@/components/common/ResetFiltersButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, X } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntryModal } from "@/components/common/EntryModal";
import { InvestorSummary } from "@/components/investor/InvestorSummary";
import { InvestorOverview } from "@/components/investor/InvestorOverview";
import { InvestorTable } from "@/components/investor/InvestorTable";
import { InvestorKanban } from "@/components/investor/InvestorKanban";
import { InvestorMeetings } from "@/components/investor/InvestorMeetings";
import { investorFields } from "@/components/investor/investorFields";
import { api } from "@/lib/api";
import { INVESTOR_TYPES, INVESTOR_STAGE_OPTS, INVESTOR_PRIORITY } from "@/data/investorOptions";

const today = () => new Date().toISOString().slice(0, 10);
const PRANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };

const VIEWS = ["overview", "table", "kanban", "meetings"];
const normFilter = (f) => ({ "due": "due", "followups-due": "due", "overdue": "overdue", "followups-overdue": "overdue", "meetings": "meetings", "no-next-action": "no-next-action" }[f] || null);

export default function InvestorPipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [investors, setInvestors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [bump, setBump] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [stageF, setStageF] = useState("all");
  const [prioF, setPrioF] = useState("all");

  const rawFilter = normFilter(searchParams.get("filter"));
  const rawView = searchParams.get("view") === "followups" ? "meetings" : searchParams.get("view");
  const view = VIEWS.includes(rawView) ? rawView : (["due", "overdue", "meetings"].includes(rawFilter) ? "meetings" : "overview");
  const sort = searchParams.get("sort") === "weighted-value" ? "weighted" : (searchParams.get("sort") || "followup");

  const changeView = (v) => { const p = new URLSearchParams(searchParams); p.set("view", v); setSearchParams(p); };
  const setSort = (v) => { const p = new URLSearchParams(searchParams); if (v === "followup") p.delete("sort"); else p.set("sort", v === "weighted" ? "weighted-value" : v); setSearchParams(p); };
  const clearFilters = () => { const p = new URLSearchParams(searchParams); p.delete("filter"); setSearchParams(p); };

  const load = useCallback(async () => {
    try { setInvestors(await api.list("investors")); } catch { setInvestors([]); } finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load, bump]);
  const refresh = () => setBump((b) => b + 1);

  // Direct investor deep link: /investors?investor=<id> -> detail route (or safe toast)
  useEffect(() => {
    const id = searchParams.get("investor");
    if (!id || !loaded) return;
    if (investors.some((i) => i.id === id)) navigate(`/investors/${id}`);
    else { toast.error("That investor is unavailable — it may have been deleted."); const p = new URLSearchParams(searchParams); p.delete("investor"); setSearchParams(p, { replace: true }); }
  }, [searchParams, loaded, investors, navigate, setSearchParams]);

  const openAdd = () => setModalOpen(true);
  const save = async (v) => { await api.create("investors", v); toast.success("Investor added"); refresh(); };
  const changeStage = async (id, stage) => { await api.update("investors", id, { stage }); toast.success("Stage updated"); refresh(); };

  const filtered = useMemo(() => {
    const t = today();
    const in7 = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
    let rows = [...investors];
    if (q) { const s = q.toLowerCase(); rows = rows.filter((i) => i.name.toLowerCase().includes(s) || (i.contact_person || "").toLowerCase().includes(s)); }
    if (typeF !== "all") rows = rows.filter((i) => i.investor_type === typeF);
    if (stageF !== "all") rows = rows.filter((i) => i.stage === stageF);
    if (prioF !== "all") rows = rows.filter((i) => i.priority === prioF);
    if (rawFilter === "due") rows = rows.filter((i) => i.next_follow_up_date && i.next_follow_up_date >= t && i.next_follow_up_date <= in7);
    else if (rawFilter === "overdue") rows = rows.filter((i) => i.next_follow_up_date && i.next_follow_up_date < t);
    else if (rawFilter === "meetings") rows = rows.filter((i) => i.next_meeting_date && i.next_meeting_date >= t);
    else if (rawFilter === "no-next-action") rows = rows.filter((i) => !(i.next_action && i.next_action.trim()));
    const wv = (i) => ((i.probability || 0) / 100) * (i.potential_amount || 0);
    const sorters = {
      followup: (a, b) => (a.next_follow_up_date || "9999").localeCompare(b.next_follow_up_date || "9999"),
      potential: (a, b) => (b.potential_amount || 0) - (a.potential_amount || 0),
      probability: (a, b) => (b.probability || 0) - (a.probability || 0),
      relevance: (a, b) => (b.relevance || 0) - (a.relevance || 0),
      priority: (a, b) => (PRANK[b.priority] || 0) - (PRANK[a.priority] || 0),
      weighted: (a, b) => wv(b) - wv(a),
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return rows.sort(sorters[sort] || sorters.followup);
  }, [investors, q, typeF, stageF, prioF, sort, rawFilter]);

  const filterLabel = { due: "follow-ups due (next 7 days)", overdue: "overdue follow-ups", meetings: "upcoming meetings", "no-next-action": "investors with no next action" }[rawFilter];

  const onOpen = (i) => navigate(`/investors/${i.id}`);
  const t = today();

  const Toolbar = (
    <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
      <div className="relative flex-1">
        <Search size={16} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6B8C]" />
        <Input data-testid="investors-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search investors…" className="rounded-full border-[#E4E9F3] bg-white pl-9 focus-visible:ring-[#1D4ED8]/40" />
      </div>
      <div className="flex flex-wrap gap-2">
        <F testid="filter-type" v={typeF} on={setTypeF} label="Type" options={INVESTOR_TYPES} />
        <F testid="filter-stage" v={stageF} on={setStageF} label="Stage" options={INVESTOR_STAGE_OPTS} />
        <F testid="filter-priority" v={prioF} on={setPrioF} label="Priority" options={INVESTOR_PRIORITY} />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="investors-sort" className="w-auto min-w-[140px] rounded-full border-[#E4E9F3] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="followup">Sort: Next follow-up</SelectItem>
            <SelectItem value="potential">Sort: Potential amount</SelectItem>
            <SelectItem value="probability">Sort: Probability</SelectItem>
            <SelectItem value="weighted">Sort: Weighted value</SelectItem>
            <SelectItem value="relevance">Sort: Relevance</SelectItem>
            <SelectItem value="priority">Sort: Priority</SelectItem>
            <SelectItem value="name">Sort: Name</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="Investor Pipeline"
        title="Investor Pipeline"
        description="An internal CRM for researching, prioritising, contacting and managing prospective investors for Edju Cat."
        actions={<Button data-testid="add-investor-btn" onClick={openAdd} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Plus size={16} /> Add Investor</Button>}
      />

      <div className="mb-8"><InvestorSummary refreshKey={bump} /></div>

      {filterLabel && (
        <div data-testid="investors-filter-banner" className="mb-4 flex items-center justify-between rounded-xl bg-[#1D4ED8]/10 px-3 py-2 text-xs font-semibold text-[#1D4ED8]">
          <span data-testid="investors-filter-label">Filtered to {filterLabel} · {filtered.length} matching</span>
          <button data-testid="clear-investor-filter" onClick={clearFilters} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear filter</button>
        </div>
      )}

      <ResetFiltersButton /><Tabs value={view} onValueChange={changeView} className="w-full">
        <TabsList data-testid="investor-tabs" className="mb-6 flex h-auto flex-wrap justify-start gap-1.5 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-sm">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:var(--accent-primary)]">Overview</TabsTrigger>
          <TabsTrigger value="table" data-testid="tab-table" className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:var(--accent-primary)]">Table</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="tab-kanban" className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:var(--accent-primary)]">Kanban</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meetings" className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:var(--accent-primary)]">Meetings &amp; Follow-ups</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><InvestorOverview investors={filtered} today={t} onOpen={onOpen} /></TabsContent>
        <TabsContent value="table">{Toolbar}<InvestorTable investors={filtered} today={t} onOpen={onOpen} onAdd={openAdd} firstRun={investors.length === 0} /></TabsContent>
        <TabsContent value="kanban">{Toolbar}<InvestorKanban investors={filtered} today={t} onOpen={onOpen} onStageChange={changeStage} /></TabsContent>
        <TabsContent value="meetings"><InvestorMeetings investors={investors} today={t} onOpen={onOpen} /></TabsContent>
      </Tabs>

      <EntryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title="Add Investor"
        submitLabel="Add Investor"
        fields={investorFields()}
        initialValues={{ investor_type: "Angel investor", stage: "Pitch deck sent / Applied", priority: "Medium", relevance: "3", probability: 0 }}
        onSubmit={save}
      />
    </>
  );
}

const F = ({ testid, v, on, label, options }) => (
  <Select value={v} onValueChange={on}>
    <SelectTrigger data-testid={testid} className="w-auto min-w-[120px] rounded-full border-[#E4E9F3] bg-white"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{label}: All</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);
